export const CONSENT_VERSION = 1;
export const CONSENT_CHANGE_EVENT = 'bmg-consent-change';
export const OPEN_CONSENT_SETTINGS_EVENT = 'bmg-open-consent-settings';

export function openConsentSettings() {
  window.dispatchEvent(new Event(OPEN_CONSENT_SETTINGS_EVENT));
}

const STORAGE_KEY = 'bmg-cookie-consent';
const VALID_MONTHS = 6;
const GOOGLE_AD_COOKIES = ['_gcl_au', '_gcl_aw'];

export interface ConsentChoice {
  version: number;
  date: string;
  marketing: boolean;
}

type GtagArgs = [string, ...unknown[]];

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: GtagArgs) => void;
  }
}

export function gtag(...args: GtagArgs) {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtagShim() {
      // gtag.js expects the Arguments object itself, not an array.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }
  window.gtag(...args);
}

function isExpired(isoDate: string): boolean {
  const saved = new Date(isoDate);
  if (Number.isNaN(saved.getTime())) return true;
  const expires = new Date(saved);
  expires.setMonth(expires.getMonth() + VALID_MONTHS);
  return Date.now() >= expires.getTime();
}

export function readConsent(): ConsentChoice | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentChoice>;
    if (parsed.version !== CONSENT_VERSION || typeof parsed.date !== 'string' || typeof parsed.marketing !== 'boolean') {
      return null;
    }
    if (isExpired(parsed.date)) return null;
    return { version: parsed.version, date: parsed.date, marketing: parsed.marketing };
  } catch {
    return null;
  }
}

export function hasMarketingConsent(): boolean {
  return readConsent()?.marketing === true;
}

function adConsentState(granted: boolean) {
  const value = granted ? 'granted' : 'denied';
  return { ad_storage: value, ad_user_data: value, ad_personalization: value, analytics_storage: 'denied' };
}

function deleteGoogleAdCookies() {
  const host = window.location.hostname;
  const domains = new Set(['', host, `.${host}`, 'bmgas.no', '.bmgas.no']);
  for (const name of GOOGLE_AD_COOKIES) {
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ''}`;
    }
  }
}

export function initConsentMode() {
  gtag('consent', 'default', adConsentState(false));
  gtag('set', 'ads_data_redaction', true);
  gtag('set', 'url_passthrough', false);
  if (hasMarketingConsent()) gtag('consent', 'update', adConsentState(true));
}

export function saveConsent(marketing: boolean): ConsentChoice {
  const choice: ConsentChoice = { version: CONSENT_VERSION, date: new Date().toISOString(), marketing };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(choice));
  } catch {
    // Storage blocked: the choice still applies for this visit.
  }
  gtag('consent', 'update', adConsentState(marketing));
  if (!marketing) deleteGoogleAdCookies();
  window.dispatchEvent(new CustomEvent<ConsentChoice>(CONSENT_CHANGE_EVENT, { detail: choice }));
  return choice;
}
